import * as React from "react";
import { DynamicFieldSelection, Field, FieldData, FieldValidation, SupportConfig, VisibilityCondition, getSalesforceFieldName } from "./types";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Typography from "@mui/joy/Typography";
import InfoIcon from "@mui/icons-material/Info";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import MultiEmailInput from "./MultiEmailInput";
import MultiSelectOptions from "./MultiSelectOptions";
import MultiSelectDynamicOptions from "./MultiSelectDynamicOptions";
import InputSection from "./InputsSection";

type InputFieldProps = {
  selectedServiceTeam: string;
  field: Field;
  selectedFields: SupportConfig;
  setSelectedFields: React.Dispatch<React.SetStateAction<SupportConfig | undefined>>;
  dynamicFieldSelection: DynamicFieldSelection;
  setDynamicFieldSelection: React.Dispatch<React.SetStateAction<DynamicFieldSelection>>;
  fieldData: FieldData;
  setFieldData: React.Dispatch<React.SetStateAction<FieldData>>;
  integrationHubUser: any;
  fieldValid: FieldValidation;
  setFieldValid: React.Dispatch<React.SetStateAction<FieldValidation>>;
  allFieldsData: Field[];
};

export default function InputField({
  selectedServiceTeam,
  selectedFields,
  dynamicFieldSelection,
  setDynamicFieldSelection,
  field,
  setFieldData,
  fieldData,
  integrationHubUser,
  fieldValid,
  setFieldValid,
  setSelectedFields,
  allFieldsData,
}: InputFieldProps) {

  // Helper function to check if field should be visible based on visibility conditions
  const isFieldVisible = (visibilityCondition: VisibilityCondition | undefined): boolean => {
    if (!visibilityCondition?.from) return true;
    
    const selectedValue = dynamicFieldSelection[visibilityCondition.from];
    if (!selectedValue) return visibilityCondition.defaultVisibility !== false;

    // Handle both single values and semicolon-separated multi-select values
    const selectedValues = selectedValue.includes(';') ? selectedValue.split(';').map(v => v.trim()) : [selectedValue];
    return selectedValues.some(value => visibilityCondition.if.includes(value));
  };

  if (!isFieldVisible(field.visibilityCondition)) return <></>;

  // Handle section field type
  if (field.type === "section") {
    return (
      <InputSection {...field}>
        {field.sectionFields?.map((sectionField, idx) => (
          <InputField
            key={`${selectedServiceTeam}_${field.name}_${sectionField.name}_${idx}`}
            selectedServiceTeam={selectedServiceTeam}
            dynamicFieldSelection={dynamicFieldSelection}
            setDynamicFieldSelection={setDynamicFieldSelection}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            field={sectionField}
            fieldData={fieldData}
            setFieldData={setFieldData}
            integrationHubUser={integrationHubUser}
            fieldValid={fieldValid}
            setFieldValid={setFieldValid}
            allFieldsData={allFieldsData}
          />
        ))}
      </InputSection>
    );
  }

  return (
    <FormControl
      sx={{
        display: field.visible === false ? "none" : "block",
      }}
    >
      <FormLabel
        sx={{
          mt: 2,
        }}
        id={`${selectedServiceTeam}_${field.name}_fl`}
      >
        {field.displayName} {field.required ? <sup>*</sup> : ""}
      </FormLabel>

      {field.type !== "information" &&
        (() => {
          if (field.options && field.options.length > 0 && field.multiSelect)
            return (
              <MultiSelectOptions
                field={field}
                value={(field.salesforce?.fieldName && fieldData[getSalesforceFieldName(field.salesforce)]) || ""}
                onChange={(value) => {
                  if (field.salesforce?.fieldName) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: value });
                  }
                  // Update dynamicFieldSelection with the comma-separated values for dependent fields
                  setDynamicFieldSelection({ ...dynamicFieldSelection, [field.name]: value });
                }}
                disabled={field.editable === false}
                selectedServiceTeam={selectedServiceTeam}
              />
            );

          if (field.options && field.options.length > 0)
            return (
              <Select
                required={field.required}
                id={`${selectedServiceTeam}_${field.name}`}
                defaultValue={field.default}
                onChange={(_event, value) => {
                  if (field.salesforce?.fieldName && value) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: value });
                  }
                  if (value) setDynamicFieldSelection({ ...dynamicFieldSelection, [field.name]: value });
                }}
              >
                {field.options?.map((option) => {
                  return (
                    <Option key={`${selectedServiceTeam}_${field.name}_${option}_opt`} value={option}>
                      {option}
                    </Option>
                  );
                })}
              </Select>
            );

          if (field.dynamicOptions && field.multiSelect) {
            return (
              <MultiSelectDynamicOptions
                field={field}
                value={(field.salesforce?.fieldName && fieldData[getSalesforceFieldName(field.salesforce)]) || ""}
                onChange={(value) => {
                  if (field.salesforce?.fieldName) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: value });
                  }
                  // Update dynamicFieldSelection with the comma-separated values for dependent fields
                  setDynamicFieldSelection({ ...dynamicFieldSelection, [field.name]: value });
                }}
                disabled={field.editable === false}
                selectedServiceTeam={selectedServiceTeam}
                dynamicFieldSelection={dynamicFieldSelection}
                allFieldsData={allFieldsData}
              />
            );
          }

          if (field.dynamicOptions && field.dynamicOptions.mappingFrom in dynamicFieldSelection) {
            // Handle both single and multi-select values for mapping lookup
            const mappingSourceValue = dynamicFieldSelection[field.dynamicOptions.mappingFrom];
            const firstSelectedValue = mappingSourceValue.includes(';') ? mappingSourceValue.split(';')[0].trim() : mappingSourceValue;
            const mappingOptions = field.dynamicOptions.mapping[firstSelectedValue] || [];
            
            return (
              <Select
                disabled={field.editable === false}
                required={field.required}
                id={`${selectedServiceTeam}_${field.name}`}
                value={
                  (field.editable === false &&
                    mappingOptions.length === 1 &&
                    mappingOptions[0]) ||
                  (field.salesforce?.fieldName && fieldData[getSalesforceFieldName(field.salesforce)]) ||
                  null
                }
                onChange={(_event, value) => {
                  if (field.salesforce?.fieldName && value) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: value });
                  }
                  if (value) setDynamicFieldSelection({ ...dynamicFieldSelection, [field.name]: value });
                }}
              >
                {mappingOptions.map((option) => {
                  return (
                    <Option key={`${selectedServiceTeam}_${field.name}_${option}_opt`} value={option}>
                      {option}
                    </Option>
                  );
                })}
              </Select>
            );
          }

          if (field.dynamicOptions && !(field.dynamicOptions.mappingFrom in dynamicFieldSelection))
            return (
              <Input
                required={isFieldVisible(field.visibilityCondition) ? field.required : false}
                id={`${selectedServiceTeam}_${field.name}`}
                placeholder={`Please select an option from '${
                  allFieldsData.find((f) => f.name === field?.dynamicOptions?.mappingFrom)?.displayName
                }' first.`}
                disabled={true}
              />
            );

          if (field.type === "date" && (field.requireFutureDate || field.requirePastDate))
            return (
              <Input
                id={`${selectedServiceTeam}_${field.name}`}
                slotProps={{
                  input: {
                    min: field.requireFutureDate ? new Date().toISOString().split("T")[0] : undefined,
                    max: field.requirePastDate ? new Date().toISOString().split("T")[0] : undefined,
                  },
                }}
                onChange={(event) => {
                  if (field.salesforce?.fieldName) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: event.target.value });
                  }
                }}
                type={field.type}
                required={isFieldVisible(field.visibilityCondition) ? field.required : false}
                placeholder={""}
                disabled={field.editable === false}
                defaultValue={field.valueFrom ? integrationHubUser[field.valueFrom] : field.default ? field.default : ""}
              />
            );

          if (field.type === "mail" && field.multiSelect) {
            return (
              <MultiEmailInput
                field={field}
                value={(field.salesforce?.fieldName && fieldData[getSalesforceFieldName(field.salesforce)]) || ""}
                onChange={(value) => {
                  if (field.salesforce?.fieldName) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: value });
                  }
                }}
                disabled={field.editable === false}
              />
            );
          }

          if (field.multiLine) {
            return (
              <Textarea
                id={`${selectedServiceTeam}_${field.name}`}
                onChange={(e) => {
                  if (field.salesforce?.fieldName) {
                    setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: e.target.value });
                  }
                }}
                required={isFieldVisible(field.visibilityCondition) ? field.required : false}
                placeholder={""}
                disabled={field.editable === false}
                defaultValue={field.valueFrom ? integrationHubUser[field.valueFrom] : field.default ? field.default : ""}
              ></Textarea>
            );
          }

          return (
            <Input
              id={`${selectedServiceTeam}_${field.name}`}
              placeholder={field.example || ""}
              error={field.validation ? !fieldValid[field.salesforce?.fieldName ? getSalesforceFieldName(field.salesforce) : field.name] === false : false}
              onChange={(event) => {
                if (field.validation?.pattern && event.target.value.length > 0) {
                  const isValid = RegExp(field.validation.pattern).test(event.target.value);
                  setFieldValid({
                    ...fieldValid,
                    [field.salesforce?.fieldName ? getSalesforceFieldName(field.salesforce) : field.name]: !isValid,
                  });
                } else {
                  setFieldValid({
                    ...fieldValid,
                    [field.salesforce?.fieldName ? getSalesforceFieldName(field.salesforce) : field.name]: false,
                  });
                }
                if (field.salesforce?.fieldName) {
                  setFieldData({ ...fieldData, [getSalesforceFieldName(field.salesforce)]: event.target.value });
                }
              }}
              type={field.type}
              required={
                !field.visibilityCondition?.from || field.visibilityCondition?.if.includes(dynamicFieldSelection[field.visibilityCondition?.from || ""])
                  ? field.required
                  : false
              }
              disabled={field.editable === false}
              defaultValue={field.valueFrom ? integrationHubUser[field.valueFrom] : field.default ? field.default : ""}
            />
          );
        })()}

      {field.additionalInformation ? (
        <Typography mt={1} level="body-xs" startDecorator={<InfoIcon color="primary"></InfoIcon>}>
          {field.additionalInformation}
        </Typography>
      ) : (
        <Typography mt={3} level="body-sm">
          {" "}
        </Typography>
      )}
    </FormControl>
  );
}
